import React, { useState, useEffect } from 'react';
import { colors, baseStyles, formatUSD, formatNum } from './SharedUI';

export default function TradingCompassSection({ symbol, liveDataRef, smartMoneyRef }) {
  const [reports, setReports] = useState([]);
  const [selectedTimeframe, setSelectedTimeframe] = useState('1h');
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  useEffect(() => {
    setReports([]);
  }, [symbol]);

  const timeframes = [
    { id: '1h', label: '1 Giờ trước', interval: '1h', limit: 2, oiPeriod: '1h' },
    { id: '2h', label: '2 Giờ trước', limit: 2, interval: '2h', oiPeriod: '2h' },
    { id: '4h', label: '4 Giờ trước', interval: '4h', limit: 2, oiPeriod: '4h' },
    { id: '12h', label: '12 Giờ trước', interval: '12h', limit: 2, oiPeriod: '12h' },
    { id: '1d', label: 'Hôm qua (D-1)', interval: '1d', limit: 2, oiPeriod: '1d' },
    { id: '2d', label: '2 Ngày trước (D-2)', interval: '1d', limit: 3, oiPeriod: '1d' }
  ];

  const runAnalysis = async () => {
    const currentLive = liveDataRef.current;
    const currentSM = smartMoneyRef.current;

    if (!currentLive || !currentSM) {
      alert("Hệ thống chưa nạp đủ dữ liệu Live & Smart Money. Vui lòng chờ vài giây!");
      return;
    }

    setIsAnalyzing(true);
    try {
      const opt = timeframes.find(t => t.id === selectedTimeframe);
      const targetIndex = opt.limit - 2;

      const klineRes = await fetch(`https://fapi.binance.com/fapi/v1/klines?symbol=${symbol}&interval=${opt.interval}&limit=${opt.limit}`);
      const klineData = await klineRes.json();
      const k = klineData[targetIndex];

      let histOI = 0;
      try {
        const oiRes = await fetch(`https://fapi.binance.com/futures/data/openInterestHist?symbol=${symbol}&period=${opt.oiPeriod}&limit=${opt.limit}`);
        if (oiRes.ok) {
          const oiData = await oiRes.json();
          if (oiData && oiData.length > targetIndex) histOI = parseFloat(oiData[targetIndex].sumOpenInterest);
        }
      } catch (e) {}

      let histFR = 0;
      try {
        const fundRes = await fetch(`https://fapi.binance.com/fapi/v1/fundingRate?symbol=${symbol}&limit=50`);
        const fundData = await fundRes.json();
        const closestFund = fundData.slice().reverse().find(f => f.fundingTime <= k[6]);
        if(closestFund) histFR = parseFloat(closestFund.fundingRate);
      } catch (e) {}

      const histData = { high: parseFloat(k[2]), low: parseFloat(k[3]), oiContract: histOI || currentLive.oiContract, fundingRate: histFR };

      const isBreakout = currentLive.last > histData.high;
      const isBreakdown = currentLive.last < histData.low;
      const isOiIncreasing = currentLive.oiContract > histData.oiContract;
      const isCvdPositive = currentLive.cvd > 0;
      
      const frLive = currentLive.fundingRate || 0;
      
      const totalVol = currentSM.longTradersQty + currentSM.shortTradersQty;
      const whaleLongVolPct = totalVol > 0 ? (currentSM.longWhalesQty / totalVol) * 100 : 0;
      const retailLongVolPct = totalVol > 0 ? ((currentSM.longTradersQty - currentSM.longWhalesQty) / totalVol) * 100 : 0;

      const longAvg = currentSM.longWhalesAvgEntryPrice;
      const shortAvg = currentSM.shortWhalesAvgEntryPrice;
      const lastPrice = currentLive.last;

      const getLongAvgColor = (avg, current) => avg > current ? colors.red : colors.green;  
      const getShortAvgColor = (avg, current) => avg > current ? colors.green : colors.red; 
      
      const priceColor = isBreakout ? colors.green : (isBreakdown ? colors.red : colors.textMain);
      const oiColor = isOiIncreasing ? colors.green : colors.red;
      const cvdColor = isCvdPositive ? colors.green : colors.red;
      const frLiveColor = frLive > 0.0001 ? colors.warn : (frLive < 0 ? colors.green : colors.textMain); 

      const priceDataJsx = (
        <>
          Live: <strong style={{color: priceColor}}>${formatNum(lastPrice)}</strong> | {' '}
          Hist High: <strong style={{color: colors.textMain}}>${formatNum(histData.high)}</strong> | {' '}
          Hist Low: <strong style={{color: colors.textMain}}>${formatNum(histData.low)}</strong>
        </>
      );

      const flowDataJsx = (
        <>
          Live OI: <strong style={{color: oiColor}}>{formatNum(currentLive.oiContract)}</strong> | {' '}
          Hist OI: <strong style={{color: colors.textMain}}>{formatNum(histData.oiContract)}</strong> | {' '}
          Live CVD: <strong style={{color: cvdColor}}>{isCvdPositive ? '+' : ''}{formatUSD(currentLive.cvd)}</strong>
        </>
      );

      const fundingDataJsx = (
        <>
          Live FR: <strong style={{color: frLiveColor}}>{frLive > 0 ? '+' : ''}{(frLive*100).toFixed(4)}%</strong> | {' '}
          Hist FR: <strong style={{color: colors.textMain}}>{histFR > 0 ? '+' : ''}{(histFR*100).toFixed(4)}%</strong>
        </>
      );

      const smDataJsx = (
        <>
          <span>Whale <strong style={{color: colors.green}}>Long</strong> Vol: {whaleLongVolPct.toFixed(1)}% | Retail <strong style={{color: colors.green}}>Long</strong> Vol: {retailLongVolPct.toFixed(1)}%</span><br/>
          <span style={{ display: 'inline-block', marginTop: '4px' }}>
            <span style={{color: colors.textSub}}>↳ [Entry Cá Mập]: </span> 
            <strong style={{color: colors.green}}>Long:</strong> <strong style={{ color: getLongAvgColor(longAvg, lastPrice) }}>${formatNum(longAvg)}</strong> | {' '}
            <strong style={{color: colors.red}}>Short:</strong> <strong style={{ color: getShortAvgColor(shortAvg, lastPrice) }}>${formatNum(shortAvg)}</strong>
          </span>
        </>
      );

      let status = ""; let priceLogic = ""; let flowLogic = ""; let frLogic = ""; let smLogic = ""; let action = ""; let colorMain = "";

      if (frLive > 0.00015) {
        frLogic = `Tâm lý Fomo quá nóng! Phe Long đang trả mức phí rất cao (${(frLive*100).toFixed(4)}%) để giữ lệnh. Cực kỳ dễ xảy ra Long Squeeze (Diệt Long) bất thình lình.`;
      } else if (frLive < -0.0001) {
        frLogic = `Phe Short đang quá đông và hoảng loạn, phải trả phí cho Long. Rất dễ có nến bật mạnh lên để diệt Short (Short Squeeze).`;
      } else {
        frLogic = `Tâm lý trung tính (${(frLive*100).toFixed(4)}%). Các nhà giao dịch không bị lệch phe quá mức.`;
      }

      if (isBreakout && !isOiIncreasing) {
        status = "🚨 CẢNH BÁO BẪY TĂNG GIÁ (BULL TRAP / SHORT SQUEEZE)"; colorMain = colors.warn;
        priceLogic = `Giá Live đang LỚN HƠN mốc High ${opt.label}. Biểu đồ phá đỉnh nhìn rất Đẹp / Bullish.`;
        flowLogic = `BẤT THƯỜNG! Giá phá đỉnh nhưng Tổng OI Live lại NHỎ HƠN OI Lịch sử. Lực mua vọt lên do phe Short cắt lỗ, không phải tiền thật bơm vào.`;
        smLogic = whaleLongVolPct > retailLongVolPct 
          ? `Cá mập vẫn nắm Volume Long (${whaleLongVolPct.toFixed(1)}%), nhưng tổng OI giảm cho thấy họ đang chốt lời chứ không nhồi thêm lệnh.`
          : `Nhỏ lẻ đang FOMO nắm Volume Long (${retailLongVolPct.toFixed(1)}%), trong khi Cá mập không mặn mà đẩy giá.`;
        action = "KHÔNG FOMO lệnh Long. Lực tăng rỗng ruột (không có OI mới). Canh giá kiệt sức để tìm điểm đảo chiều (Short).";
      } else if (isBreakout && isOiIncreasing && isCvdPositive) {
        status = "🟢 XÁC NHẬN SÓNG TĂNG UY TÍN (TRUE BREAKOUT)"; colorMain = colors.green;
        priceLogic = `Giá Live đã PHÁ VỠ mốc High ${opt.label}. Mở ra xu hướng tăng mới.`;
        flowLogic = `DÒNG TIỀN THỰC! Tổng OI Live TĂNG so với lịch sử, kèm CVD Dương. Tiền thật đang nạp vào sòng để đẩy giá lên.`;
        smLogic = whaleLongVolPct > retailLongVolPct
          ? `Cá mập phe Long đang dẫn dắt cuộc chơi (chiếm ${whaleLongVolPct.toFixed(1)}% Volume).`
          : `Nhỏ lẻ đang chiếm ưu thế Long, rủi ro rung lắc nhẹ để rũ hàng (Wash out) trước khi tăng tiếp.`;
        action = frLive > 0.00015 ? "Xu hướng tăng khỏe nhưng Fomo hơi cao. Chờ nhịp rũ hàng (Funding hạ nhiệt) rồi mới Long." : "Xu hướng tăng bền vững có tiền thật bảo kê. Khuyến nghị canh nhịp điều chỉnh (Pullback) để thuận theo xu hướng Long.";
      } else {
        status = "⚖️ THỊ TRƯỜNG ĐI NGANG / CHỜ XÁC NHẬN (RANGE BOUND)"; colorMain = colors.textSub;
        priceLogic = `Giá Live đang NẰM TRONG biên độ giao dịch của ${opt.label}. Chưa rõ phe nào thắng.`;
        flowLogic = `Thanh khoản đi ngang. Khối lượng OI không có sự đột biến đáng kể so với lịch sử.`;
        smLogic = `Cá Mập đang gồng các vị thế Entry. Quan sát thêm để xem họ sẽ đẩy giá theo hướng nào có lợi nhất.`;
        action = "Hạn chế giao dịch tại vùng giá lơ lửng. Chờ giá phá vỡ rõ ràng các mốc cản của Lịch sử (High/Low) kèm khối lượng đột biến.";
      }

      const newReport = {
        id: Date.now(), time: new Date().toLocaleTimeString('vi-VN') + ' - ' + new Date().toLocaleDateString('vi-VN'),
        targetLabel: opt.label, status, colorMain,
        priceDataJsx, priceLogic,
        flowDataJsx, flowLogic,
        fundingDataJsx, frLogic, 
        smDataJsx, smLogic, action
      };

      setReports(prev => [newReport, ...prev]);
    } catch (error) { console.error(error); } finally { setIsAnalyzing(false); }
  };

  return (
    <div style={{ ...baseStyles.card, marginTop: '24px', border: `1px solid ${colors.border}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${colors.border}`, paddingBottom: '12px', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <strong style={{ color: colors.textMain, fontSize: '16px' }}>🤖 TRADING COMPASS & INSIGHTS LOG</strong>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '13px', color: colors.textSub }}>Chọn mốc đối chiếu:</span>
          <select value={selectedTimeframe} onChange={(e) => setSelectedTimeframe(e.target.value)} style={baseStyles.select}>
            {timeframes.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
          <button onClick={runAnalysis} disabled={isAnalyzing} style={{ backgroundColor: colors.cardBg, color: colors.textMain, border: `1px solid ${colors.textMain}`, padding: '6px 16px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}>
            {isAnalyzing ? 'ĐANG PHÂN TÍCH...' : '🔍 PHÂN TÍCH NGAY'}
          </button>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxHeight: '600px', overflowY: 'auto', paddingRight: '4px' }}>
        {reports.length === 0 && <div style={{ color: colors.textSub, fontSize: '13px', textAlign: 'center', padding: '20px 0' }}>Bấm nút "Phân Tích Ngay" để máy tính chụp snapshot dữ liệu và đưa ra nhận định.</div>}
        {reports.map((r, idx) => (
          <div key={r.id} style={{ backgroundColor: 'rgba(0,0,0,0.2)', padding: '16px', borderRadius: '8px', borderLeft: `4px solid ${r.colorMain}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: colors.textSub, marginBottom: '8px', borderBottom: `1px dotted ${colors.border}`, paddingBottom: '8px' }}>
              <span>{idx === 0 ? '▼ BẢN BÁO CÁO MỚI NHẤT' : '▼ BẢN BÁO CÁO CŨ HƠN'}</span>
              <span>Tạo lúc: {r.time}</span>
            </div>
            <div style={{ fontSize: '13px', color: colors.textSub, marginBottom: '8px' }}>⏱️ HỆ QUY CHIẾU: LIVE vs MỐC <strong>{r.targetLabel.toUpperCase()}</strong></div>
            <div style={{ fontSize: '14px', fontWeight: 'bold', color: r.colorMain, marginBottom: '16px' }}>{r.status}</div>
            <div style={{ fontSize: '13px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div><strong style={{color: colors.textMain}}>1. TÍN HIỆU HÀNH ĐỘNG GIÁ</strong><br/><span style={{color: colors.textSub}}>↳ [Dữ liệu]: </span>{r.priceDataJsx}<br/><span style={{color: colors.textSub}}>↳ [Logic]  :</span> {r.priceLogic}</div>
              <div><strong style={{color: colors.textMain}}>2. KIỂM CHỨNG DÒNG TIỀN THỰC</strong><br/><span style={{color: colors.textSub}}>↳ [Dữ liệu]: </span>{r.flowDataJsx}<br/><span style={{color: colors.textSub}}>↳ [Logic]  :</span> {r.flowLogic}</div>
              <div><strong style={{color: colors.textMain}}>3. ĐO LƯỜNG TÂM LÝ (FUNDING RATE)</strong><br/><span style={{color: colors.textSub}}>↳ [Dữ liệu]: </span>{r.fundingDataJsx}<br/><span style={{color: colors.textSub}}>↳ [Logic]  :</span> {r.frLogic}</div>
              <div><strong style={{color: colors.textMain}}>4. XÁC NHẬN BỞI CÁ MẬP</strong><br/><span style={{color: colors.textSub}}>↳ [Dữ liệu]: </span>{r.smDataJsx}<br/><span style={{color: colors.textSub}}>↳ [Logic]  :</span> {r.smLogic}</div>
              <div style={{ backgroundColor: colors.cardBg, padding: '10px', borderRadius: '4px', border: `1px solid ${colors.border}`, marginTop: '8px' }}>
                <strong style={{color: colors.textMain}}>💡 KHUYẾN NGHỊ HÀNH ĐỘNG:</strong><br/><span style={{color: colors.textSub}}>{r.action}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
